import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import StepperForm from '../../components/feature/StepperForm';
import EducationPanel from '../../components/feature/EducationPanel';
import ComplianceDisclaimer from '../../components/feature/ComplianceDisclaimer';
import SEOHead from '../../components/feature/SEOHead';
import { getEducationContent } from '../../utils/educationMap';
import { checkQualification } from '../../utils/qualification';
import type { FormAnswers } from '../../utils/qualification';
import { track, TRACKING_EVENTS } from '../../utils/tracking';

export default function PrequalificationPage() {
  const [formAnswers, setFormAnswers] = useState<FormAnswers>({});
  const [currentQuestionId, setCurrentQuestionId] = useState<string>('');
  const [isFormComplete, setIsFormComplete] = useState(false);

  const handleFormUpdate = (answers: FormAnswers) => {
    setFormAnswers(answers);
  };

  const handleFormComplete = (answers: FormAnswers) => {
    setFormAnswers(answers);
    setIsFormComplete(true);
    track(TRACKING_EVENTS.FORM_COMPLETE, {
      ...answers
    });
  };

  const handleAnswersChange = (answers: FormAnswers, questionId?: string) => {
    setFormAnswers(answers);
    if (questionId) {
      setCurrentQuestionId(questionId);
    }
  };

  // Get qualification status and education content
  const qualificationResult = checkQualification(formAnswers);

  // Get education content with current answers
  const educationContent = getEducationContent(
    formAnswers,
    qualificationResult.isQualified,
    qualificationResult.tier
  );

  // Simple no‑op handler for "Edit Answers" – can be expanded later if needed
  const handleEditAnswers = () => {
    // Currently we just keep the form as‑is; you could scroll to the form,
    // highlight the unanswered fields, etc.
  };

  return (
    <>
      <SEOHead
        title="Business Funding Pre-Qualification | Quick Assessment"
        description="Complete our quick pre-qualification form to see what business funding options you may qualify for. No credit impact. Takes 5 minutes."
        keywords="business funding prequalification, funding assessment, business loan prequalify"
        canonical="/prequalification"
      />

      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-orange-50">
        <Header />

        <main className="flex-grow w-full">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
            {/* Hero Section */}
            <div className="text-center mb-8 sm:mb-12">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 sm:mb-4">
                Pre-Qualification Assessment
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto px-4">
                Answer a few quick questions to see what funding options your business may qualify for.{' '}
                This assessment has no impact on your credit score.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-4 sm:mt-6 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-600"></i>
                  <span>No Credit Impact</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-600"></i>
                  <span>5 Minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-user-forbid-line text-purple-600"></i>
                  <span className="hidden sm:inline">No Personal Info Needed</span>
                  <span className="sm:hidden">No Personal Info</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-lock-line text-blue-600"></i>
                  <span>Secure & Confidential</span>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Form Section - Takes 2 columns on desktop */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
                  <StepperForm 
                    onAnswersChange={handleAnswersChange}
                    onComplete={handleFormComplete}
                  />
                </div>
              </div>

              {/* Education Panel - Takes 1 column on desktop, full width on mobile */}
              <div className="lg:col-span-1">
                <EducationPanel
                  content={educationContent}
                  isQualified={qualificationResult.isQualified}
                  disqualificationReason={qualificationResult.disqualificationReason}
                  onContinue={() => {}}
                  onStartOver={() => {
                    setFormAnswers({});
                  }}
                  onEditAnswers={handleEditAnswers}
                  answers={formAnswers}
                  currentQuestionId={currentQuestionId}
                  showReadyToApply={isFormComplete}
                />
              </div>
            </div>

            {/* Compliance */}
            <div className="mt-8 sm:mt-12">
              <ComplianceDisclaimer />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
